package org.hs_soft.runmynesto.data.api_datasource.authentication

import androidx.compose.runtime.NoLiveLiterals
import kotlinx.browser.document
import kotlinx.browser.window
import org.hs_soft.runmynesto.domain.repository.network_datasource.home.FetchIdTokenRepository
import org.hs_soft.runmynesto.domain.util.ApiUtils
import org.hs_soft.runmynesto.modules.Auth
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.coroutines.suspendCoroutine
import kotlin.js.Date
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

class FetchIdTokenRepositoryImpl : FetchIdTokenRepository {

    private val refreshMutex = Mutex()

    @NoLiveLiterals
    override suspend fun getIdToken(): String = refreshMutex.withLock {
        suspendCoroutine { continuation ->
            val username = getUsernameFromCookies()
            if (username != null) {
                handleExistingUser(username, continuation)

            } else {
                handleNewUser(continuation)
            }
        }
    }

    private fun handleExistingUser(
        username: String,
        continuation: kotlin.coroutines.Continuation<String>,
    ) {
        val idToken = getCookie(name = constructCookieName(username, "idToken"))
        val refreshToken = getCookie(name = constructCookieName(username, "refreshToken"))

        if (idToken != null && !isTokenExpired(idToken)) {
            continuation.resume(idToken)
        } else if (refreshToken != null) {
            fetchNewSession(username, continuation)
        } else {
            fetchNewSession(username, continuation)
        }
    }

    private fun handleNewUser(continuation: kotlin.coroutines.Continuation<String>) {
        Auth.getCurrentUser().then { result ->
            val username = result.username
            fetchNewSession(username, continuation)
        }.catch { error ->
            console.error("Get Current User Error:", error)
            continuation.resumeWithException(Exception("Failed to get current user: $error"))
        }
    }

    private fun fetchNewSession(
        username: String,
        continuation: kotlin.coroutines.Continuation<String>
    ) {
        Auth.fetchAuthSession().then { session ->
            val newIdToken = session.tokens.idToken
            val newRefreshToken = session.tokens.refreshToken

            document.cookie = "${constructCookieName(username,
                "idToken")}=$newIdToken; path=/"
            document.cookie = "${constructCookieName(username,
                "refreshToken")}=$newRefreshToken; path=/"
            
            continuation.resume(newIdToken)
        }.catch { error: dynamic ->
            console.log("Fetch AuthData Error: $error")
            continuation.resumeWithException(Exception("Failed to get AuthData: $error"))
        }
    }

    private fun getUsernameFromCookies(): String? {
        val cookies = document.cookie.split("; ")
        val prefix = "CognitoIdentityServiceProvider.${ApiUtils.Cognito.USER_POOL_CLIENT_ID}."

        for (cookie in cookies) {
            val parts = cookie.split("=")
            val cookieName = parts[0]

            if (cookieName.startsWith(prefix) && cookieName.endsWith(".idToken")) {
                return cookieName.substringAfter(prefix).substringBefore(".idToken")
            }
        }

        return null
    }

    private fun getCookie(name: String): String? {
        val cookies = document.cookie.split("; ")
        for (cookie in cookies) {
            val parts = cookie.split("=")
            if (parts[0] == name) {
                return parts[1]
            }
        }
        return null
    }

    private fun isTokenExpired(token: String): Boolean {
        return try {
            val parts = token.split(".")
            if (parts.size < 3) return true

            // Convert Base64Url to Base64
            val base64 = parts[1]
                .replace("-", "+")
                .replace("_", "/")
                .let { input ->
                    // Add padding if needed
                    when (input.length % 4) {
                        0 -> input
                        2 -> input + "=="
                        3 -> input + "="
                        else -> input
                    }
                }

            val payload = JSON.parse<dynamic>(window.atob(base64))
            val exp = payload.exp as? Int ?: return true
            val now = (Date().getTime() / 1000).toInt()
            now >= (exp - 5*60)  // 5 minutes buffer
        } catch (e: Exception) {
            console.error("Token validation error:", e)
            true
        }
    }
}

private fun constructCookieName(userName: String, tokenType: String): String {
    return "CognitoIdentityServiceProvider.${ApiUtils.Cognito.USER_POOL_CLIENT_ID}" +
            ".$userName.$tokenType"
}